import React, { useEffect, useRef, useState } from 'react';
import { AnalysisResult } from '../types';
import ChainVisualization from './ChainVisualization';
import { addFavoriteVariable, isVariableFavorite } from '../utils/favorites';

const ResultDisplay: React.FC<{ result: AnalysisResult }> = ({ result }) => {
  const getImpactColor = (direction: string) => {
    switch (direction) {
      case 'positive':
        return 'text-green-600 bg-green-50 border-green-200';
      case 'negative':
        return 'text-red-600 bg-red-50 border-red-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getDirectionText = (direction: string) => {
    switch (direction) {
      case 'positive':
        return '正面影响';
      case 'negative':
        return '负面影响';
      default:
        return '中性影响';
    }
  };

  const renderStars = (stars: number) => {
    return (
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <span
            key={star}
            className={`text-xl ${
              star <= stars ? 'text-yellow-400' : 'text-gray-300'
            }`}
          >
            ★
          </span>
        ))}
        <span className="ml-2 text-sm text-gray-600">{stars}/5</span>
      </div>
    );
  };

  const renderImpactCard = (title: string, impact: any) => {
    return (
      <div className={`border-2 rounded-lg p-4 ${getImpactColor(impact.direction)}`}>
        <div className="flex items-center justify-between mb-2">
          <h4 className="font-bold text-lg">{title}</h4>
          <span className="text-sm font-medium">{getDirectionText(impact.direction)}</span>
        </div>
        <div className="mb-2">{renderStars(impact.strength_stars)}</div>
        <p className="text-sm text-gray-700">{impact.summary}</p>
      </div>
    );
  };

  const handleAddFactorFavorite = (variable: string) => {
    if (variable.trim() && !isVariableFavorite(variable)) {
      addFavoriteVariable(variable);
      window.dispatchEvent(new Event('favoritesUpdated'));
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6 space-y-6">
      {/* 摘要信息 */}
      <div>
        <h2 className="text-2xl font-bold text-gray-800 mb-4">分析结果</h2>
        <div className="bg-gray-50 rounded-lg p-4 mb-4">
          <p className="text-gray-700 leading-relaxed">{result.summary}</p>
        </div>

        {/* AI生成的影响变量（自动分析模式） */}
        {result.factors && result.factors.length > 0 && (
          <div className="mb-6">
            <h3 className="text-xl font-bold text-gray-800 mb-3">识别的主要影响变量</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {result.factors.map((factor, index) => (
                <div
                  key={index}
                  className="border-2 rounded-lg p-4 bg-gradient-to-br from-purple-50 to-pink-50 border-purple-200"
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-sm font-bold text-purple-600 bg-purple-100 px-2 py-1 rounded">
                          #{index + 1}
                        </span>
                        <h4 className="font-bold text-lg text-gray-800">{factor.variable}</h4>
                      </div>
                      <p className="text-sm text-gray-600 mb-2">{factor.reason}</p>
                      <div className="flex items-center gap-2 text-sm">
                        <span className={`px-2 py-1 rounded ${
                          factor.direction === 'positive' ? 'bg-green-100 text-green-700' :
                          factor.direction === 'negative' ? 'bg-red-100 text-red-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {factor.direction === 'positive' ? '正面' : factor.direction === 'negative' ? '负面' : '中性'}
                        </span>
                        <span className="text-yellow-500">{'★'.repeat(factor.strength)}{'☆'.repeat(5 - factor.strength)}</span>
                      </div>
                    </div>
                    {!isVariableFavorite(factor.variable) && (
                      <button
                        onClick={() => handleAddFactorFavorite(factor.variable)}
                        className="ml-2 text-purple-500 hover:text-purple-700 transition-colors"
                        title="收藏此变量"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                        </svg>
                      </button>
                    )}
                    {isVariableFavorite(factor.variable) && (
                      <div className="ml-2 text-purple-600" title="已收藏">
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                        </svg>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 只在非自动分析模式时显示期限影响 */}
        {!(result.factors && result.factors.length > 0) && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {renderImpactCard('短期影响（1-3个月）', result.short_term_impact)}
            {renderImpactCard('中期影响（3-24个月）', result.medium_term_impact)}
            {renderImpactCard('长期影响（24个月以上）', result.long_term_impact)}
          </div>
        )}
      </div>

      {/* 传导链条可视化 */}
      <div>
        <h3 className="text-xl font-bold text-gray-800 mb-4">传导链条</h3>
        <ChainVisualization result={result} />
      </div>

      {/* 详细节点信息 */}
      <div>
        <h3 className="text-xl font-bold text-gray-800 mb-4">传导步骤</h3>
        <div className="space-y-3">
          {result.chain.map((node, index) => (
            <div
              key={node.id}
              className="border-l-4 border-blue-500 pl-4 py-2 bg-gray-50 rounded-r"
            >
              <div className="flex items-center gap-2 mb-1">
                <span className="text-sm font-medium text-gray-500">步骤 {index + 1}</span>
                <span className="text-lg font-semibold text-gray-800">{node.label}</span>
              </div>
              <p className="text-sm text-gray-600">{node.description}</p>
            </div>
          ))}
        </div>
      </div>

      {/* 风险提示 */}
      <div className="mt-8 pt-6 border-t border-gray-200">
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-r">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-yellow-800">
                <span className="font-semibold">风险提示：</span>
                以上内容由 AI 生成，仅供参考，不作为投资建议。市场有风险，投资需谨慎。
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResultDisplay;


